import { JSX, ReactNode } from 'react';
import { Navigate } from 'react-router';

import { useUser } from '@clerk/react';

import { ALLOWED_REDIRECT_HOSTS, APP_PAGES } from './constants';
import { OverlayLoader } from '../components/OverlayLoader';
import { useInactivityTracker } from '../hooks/useInactivityTracker';
import { AppShellReady, BootFallback } from '../Layout/AppShell';
import { OnboardingStep } from '../pages/private/Onboarding/Onboarding.constants';

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps): JSX.Element {
  const { isSignedIn, isLoaded, user } = useUser();

  useInactivityTracker();

  if (!isLoaded) {
    return <BootFallback />;
  }

  if (!isSignedIn) {
    return <Navigate to={APP_PAGES.LOGIN} replace={true} />;
  }

  const hasCompletedOnboarding =
    user?.unsafeMetadata?.ht_onboarding_complete === true ||
    user?.unsafeMetadata?.ht_firstAccessModalViewed === true;
  const isOnboardingPage = window.location.pathname.startsWith(
    APP_PAGES.ONBOARDING_BASE,
  );

  if (!hasCompletedOnboarding && !isOnboardingPage) {
    return (
      <Navigate
        to={`${APP_PAGES.ONBOARDING_BASE}/${OnboardingStep.WELCOME}`}
        replace={true}
      />
    );
  }

  if (hasCompletedOnboarding && isOnboardingPage) {
    return <Navigate to={APP_PAGES.DOBOX} replace={true} />;
  }

  return <AppShellReady>{children}</AppShellReady>;
}

export function PublicRouteGuard({ children }: AuthGuardProps): JSX.Element {
  const { isSignedIn, isLoaded } = useUser();

  if (!isLoaded) {
    return <BootFallback />;
  }

  if (isSignedIn) {
    const redirectUrl = new URLSearchParams(window.location.search).get(
      'redirect_url',
    );

    if (redirectUrl) {
      try {
        const url = new URL(redirectUrl);
        if (ALLOWED_REDIRECT_HOSTS.includes(url.hostname)) {
          window.location.href = redirectUrl;
          return <OverlayLoader isVisible={true} />;
        }
      } catch {
        // Invalid URL, fall through to default redirect
      }
    }

    return <Navigate to={APP_PAGES.DOBOX} replace={true} />;
  }

  return <AppShellReady>{children}</AppShellReady>;
}

export function UniversalRouteGuard({ children }: AuthGuardProps): JSX.Element {
  const { isLoaded } = useUser();

  if (!isLoaded) {
    return <BootFallback />;
  }

  return <AppShellReady>{children}</AppShellReady>;
}
